import { useState, useEffect } from "react";
import { SuperAdminLayout } from "@/components/admin/SuperAdminLayout";
import { KanbanBoard } from "@/components/referrals/KanbanBoard";
import { ReferralManager } from "@/components/referrals/ReferralManager";
import { Card, CardContent } from "@/components/ui/card";
import { type HireFormData } from "@/components/admin/HiredConfirmationModal";
import { AdminReferral, ReferralStatus } from "@/lib/schema";
import { changeReferralStatus } from "@/lib/referralStatusUtils";
import { useAuth } from "@/hooks/useAuth";
// AdminReferralWithUI is now imported from the API
import { ReferralStatusCard } from "@/components/admin/ReferralStatusCard";
import { FilterSection, type FilterConfig } from "@/components/ui/FilterSection";
import { LoadingScreen } from "@/components/ui/LoadingScreen";
import { 
  getAdminReferralsWithUI, 
  getAdminReferralStatusCounts,
  subscribeToAdminReferralsWithUI,
  subscribeToAdminReferralStatusCounts,
  AdminReferralWithUI as AdminReferralWithUIType
} from "@/api/referrals";
import { useToast } from "@/hooks/use-toast";

// AdminReferralWithUI interface is now imported from the API

export default function ReferralManagement() {
  const { toast } = useToast();
  const { userData } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [jobFilter, setJobFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [selectedReferral, setSelectedReferral] = useState<AdminReferralWithUIType | null>(null);
  const [showReferralViewer, setShowReferralViewer] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncingReferrals, setSyncingReferrals] = useState<Set<string>>(new Set());

  // Real data from API
  const [referrals, setReferrals] = useState<AdminReferralWithUIType[]>([]);
  const [statusCounts, setStatusCounts] = useState({
    submitted: 0,
    shortlisted: 0,
    interviewed: 0,
    under_review: 0
  });

  // Load initial data and set up real-time subscriptions
  useEffect(() => {
    let unsubscribeReferrals: (() => void) | undefined;
    let unsubscribeCounts: (() => void) | undefined;

    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Load initial data
        const [initialReferrals, initialCounts] = await Promise.all([
          getAdminReferralsWithUI(),
          getAdminReferralStatusCounts()
        ]);

        setReferrals(initialReferrals);
        setStatusCounts(initialCounts);

        // Set up real-time subscriptions
        unsubscribeReferrals = subscribeToAdminReferralsWithUI((newReferrals) => {
          setReferrals(newReferrals);
        });

        unsubscribeCounts = subscribeToAdminReferralStatusCounts((newCounts) => {
          setStatusCounts(newCounts);
        });

      } catch (err) {
        console.error('Error loading referral data:', err);
        setError('Failed to load referral data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadData();

    // Cleanup subscriptions on unmount
    return () => {
      if (unsubscribeReferrals) unsubscribeReferrals();
      if (unsubscribeCounts) unsubscribeCounts();
    };
  }, []);


  const handleStatusChangeSuccess = (referralId: string, newStatus: ReferralStatus, hireData?: HireFormData) => {
    // Find the referral being updated
    const referral = referrals.find(r => r.id === referralId);
    const candidateName = referral?.candidateName || 'referral';
    
    // Immediate optimistic update - update UI instantly
    setReferrals(prev => prev.map(ref => 
      ref.id === referralId 
        ? { ...ref, status: newStatus, updatedAt: new Date() }
        : ref
    ));
    
    // Update the selected referral if it's the one being updated
    if (selectedReferral && selectedReferral.id === referralId) {
      setSelectedReferral(prev => prev ? { ...prev, status: newStatus, updatedAt: new Date() } : prev);
    }
    
    console.log(`✅ Successfully updated ${candidateName} status to ${newStatus}`, hireData ? { hireData } : '');
  };

  const handleNoteAddSuccess = (referralId: string, note: string) => {    
    // Optimistic update for better UX - add the note immediately to selectedReferral
    if (selectedReferral && selectedReferral.id === referralId && userData) {
      const newNote = {
        note: note,
        date: new Date(),
        recruiterId: userData.id
      };
      
      setSelectedReferral(prev => prev ? {
        ...prev,
        recruiterNote: [...(prev.recruiterNote || []), newNote],
        updatedAt: new Date()
      } : prev);
    }
    
    // Real-time subscription will sync with server data shortly after
  };

  const handleError = (error: string) => {
    setError(error);
    // Auto-clear error after 5 seconds
    setTimeout(() => setError(null), 5000);
  };

  // Bridge function for KanbanBoard (drag-and-drop)
  const handleStatusChange = async (referralId: string, newStatus: ReferralStatus) => {
    if (!userData) {
      setError('You must be logged in to update referral status.');
      return;
    }

    // For drag-and-drop operations, we need to optimistically update and handle errors
    const referral = referrals.find(r => r.id === referralId);
    const candidateName = referral?.candidateName || 'referral';
    
    // Immediate optimistic update for responsive UI
    handleStatusChangeSuccess(referralId, newStatus);
    
    // Add to syncing set for visual feedback
    setSyncingReferrals(prev => new Set(prev).add(referralId));
    
    // Make the actual API call
    await changeReferralStatus({
      referralId,
      newStatus,
      userId: userData.id,
      notes: `Status changed to ${newStatus} via drag and drop`,
      onSuccess: (referralId, newStatus) => {
        console.log(`✅ Successfully updated ${candidateName} status to ${newStatus}`);
        // Remove from syncing set
        setSyncingReferrals(prev => {
          const newSet = new Set(prev);
          newSet.delete(referralId);
          return newSet;
        });
        // The optimistic update is already applied, real-time subscription will sync
      },
      onError: (errorMessage, referralId, newStatus) => {
        console.error(`❌ Error updating referral status: ${errorMessage}`);
        
        // Remove from syncing set
        setSyncingReferrals(prev => {
          const newSet = new Set(prev);
          newSet.delete(referralId);
          return newSet;
        });
        
        // Revert optimistic update on failure
        setReferrals(prev => prev.map(ref => 
          ref.id === referralId 
            ? { ...ref, status: referral?.status || ref.status }
            : ref
        ));
        
        // Revert selected referral status if it's the one being updated
        if (selectedReferral && selectedReferral.id === referralId) {
          setSelectedReferral(prev => prev ? { ...prev, status: referral?.status || prev.status } : prev);
        }
        
        // Show error
        setError(`Failed to update ${candidateName} status. The change has been reverted.`);
      }
    });
  };


  const handleViewReferral = (referral: AdminReferralWithUIType) => {
    // Pass the full AdminReferralWithUI data including candidateData
    setSelectedReferral(referral);
    setShowReferralViewer(true);
  };


  const filteredReferrals = referrals.filter(referral => {
    const matchesSearch = referral.candidateName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         referral.jobTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         referral.referrerName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || referral.status === statusFilter;
    const matchesJob = jobFilter === "all" || referral.jobTitle === jobFilter;
    const matchesPriority = priorityFilter === "all" || referral.priority === priorityFilter;
    return matchesSearch && matchesStatus && matchesJob && matchesPriority;
  });

  // Status counts are now loaded from the real API and updated via subscription

  // Get unique job titles for filtering
  const uniqueJobTitles = Array.from(new Set(referrals.map(r => r.jobTitle)));

  // Filter configurations
  const statusFilterConfig: FilterConfig = {
    placeholder: "Status",
    options: [
      { value: "all", label: "All Status" },
      { value: ReferralStatus.SUBMITTED, label: "Submitted" },
      { value: ReferralStatus.SHORTLISTED, label: "Shortlisted" },
      { value: ReferralStatus.INTERVIEWED, label: "Interviewed" },
      { value: ReferralStatus.UNDER_REVIEW, label: "Under Review" }
    ]
  };

  const jobFilterConfig: FilterConfig = {
    placeholder: "Job",
    options: [
      { value: "all", label: "All Jobs" },
      ...uniqueJobTitles.map(job => ({ value: job, label: job }))
    ]
  };

  const priorityFilterConfig: FilterConfig = {
    placeholder: "Priority",
    options: [
      { value: "all", label: "All Priority" },
      { value: "high", label: "High" },
      { value: "normal", label: "Normal" },
      { value: "low", label: "Low" }
    ]
  };

  // Check if any filters are active
  const hasActiveFilters = statusFilter !== "all" || jobFilter !== "all" || priorityFilter !== "all";

  const clearFilters = () => {
    setStatusFilter("all");
    setJobFilter("all");
    setPriorityFilter("all");
  };

  // Show loading state
  if (loading) {
    return (
      <SuperAdminLayout
        title="Referral Management"
        subtitle="Manage referral pipeline, review CVs, and coordinate with clients."
      >
        <LoadingScreen />
      </SuperAdminLayout>
    );
  }

  // Show error state
  if (error) {
    return (
      <SuperAdminLayout
        title="Referral Management"
        subtitle="Manage referral pipeline, review CVs, and coordinate with clients."
      >
        <Card className="p-6 text-center">
          <CardContent>
            <p className="text-red-600 mb-4">{error}</p>
            <button 
              onClick={() => window.location.reload()} 
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Retry
            </button>
          </CardContent>
        </Card>
      </SuperAdminLayout>
    );
  }

  return (
    <SuperAdminLayout
      title="Referral Management"
      subtitle="Manage referral pipeline, review CVs, and coordinate with clients."
    >
      {/* Pipeline Overview */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-8 lg:mb-10">
        <ReferralStatusCard
          status={ReferralStatus.SUBMITTED}
          title="SUBMITTED"
          value={statusCounts.submitted}
          testId="card-submitted"
        />

        <ReferralStatusCard
          status={ReferralStatus.SHORTLISTED}
          title="SHORTLISTED"
          value={statusCounts.shortlisted}
          testId="card-shortlisted"
        />

        <ReferralStatusCard
          status={ReferralStatus.INTERVIEWED}
          title="INTERVIEWED"
          value={statusCounts.interviewed}
          testId="card-interviewed"
        />

        <ReferralStatusCard
          status={ReferralStatus.UNDER_REVIEW}
          title="UNDER REVIEW"
          value={statusCounts.under_review}
          variant="dark"
          testId="card-under-review"
        />
      </div>

      {/* Filters and Search */}
      <div className="mb-8 lg:mb-10">
        <FilterSection
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Search candidates, jobs, referrers..."
          filters={{
            status: {
              value: statusFilter,
              onChange: setStatusFilter,
              config: statusFilterConfig
            },
            job: {
              value: jobFilter,
              onChange: setJobFilter,
              config: jobFilterConfig
            },
            priority: {
              value: priorityFilter,
              onChange: setPriorityFilter,
              config: priorityFilterConfig
            }
          }}
          useCard={true}
          hasActiveFilters={hasActiveFilters}
          onClearFilters={clearFilters}
        />
      </div>

      {/* Kanban Board - Responsive Container */}
      <div className="w-full">
        <div className="min-h-[600px] lg:min-h-[700px] xl:min-h-[800px] overflow-x-auto">
          <div className="min-w-[800px] lg:min-w-full">
            <KanbanBoard 
              referrals={filteredReferrals}
              onStatusChange={handleStatusChange}
              onViewReferral={handleViewReferral}
              configType="candidate"
              syncingReferrals={syncingReferrals}
            />
          </div>
        </div>
      </div>

      {/* Referral Manager - handles all referral modals */}
      <ReferralManager
        selectedReferral={selectedReferral}
        isReferralViewerOpen={showReferralViewer}
        onCloseReferralViewer={() => setShowReferralViewer(false)}
        onStatusChangeSuccess={handleStatusChangeSuccess}
        onNoteAddSuccess={handleNoteAddSuccess}
        onError={handleError}
      />
    </SuperAdminLayout>
  );
}